class KgBwsHomepage {
  constructor() {
    this.initHeader();
    this.initReveal();
    this.initProductForms();
    this.initProductPages();
    this.initContactPages();
    this.initFaqAccordions();
  }

  initHeader() {
    const headers = document.querySelectorAll('[data-kg-header]');
    const wraps = document.querySelectorAll('[data-kg-header-wrap]');
    const fixedHeaders = [];

    headers.forEach((header) => {
      const wrap = header.closest('[data-kg-header-wrap]');
      const section = header.closest('.shopify-section');
      if (!wrap || !section || wrap.classList.contains('kg-header-wrap--static')) return;

      const placeholder = document.createElement('div');
      placeholder.className = 'kg-header-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      section.before(placeholder);
      section.classList.add('kg-fixed-header-section');
      fixedHeaders.push({ header, section, placeholder });
    });

    let ticking = false;
    const update = () => {
      fixedHeaders.forEach(({ header, section, placeholder }) => {
        const announcement = section.previousElementSibling?.matches('.kg-header-placeholder')
          ? section.previousElementSibling.previousElementSibling
          : section.previousElementSibling;
        const announcementHeight = announcement?.querySelector?.('[data-kg-announcement]')?.offsetHeight || 0;
        const headerHeight = header.offsetHeight;
        const shouldFix = window.scrollY >= announcementHeight;

        placeholder.style.setProperty('--kg-header-placeholder-height', `${headerHeight}px`);
        placeholder.classList.toggle('is-active', shouldFix);
        section.classList.toggle('is-fixed', shouldFix);
        header.classList.toggle('is-scrolled', shouldFix || window.scrollY > 16);
        header.closest('[data-kg-header-wrap]')?.classList.toggle('is-scrolled', shouldFix || window.scrollY > 12);
      });
      ticking = false;
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });

    document.querySelectorAll('[data-kg-menu-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        const header = button.closest('[data-kg-header]');
        const wrap = button.closest('[data-kg-header-wrap]');
        const expanded = header?.dataset.menuOpen === 'true';
        if (!header || !wrap) return;
        const nextExpanded = !expanded;
        header.dataset.menuOpen = nextExpanded ? 'true' : 'false';
        wrap.dataset.menuOpen = nextExpanded ? 'true' : 'false';
        button.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
        document.documentElement.classList.toggle('kg-menu-lock', nextExpanded);
      });
    });

    document.querySelectorAll('[data-kg-menu-close]').forEach((button) => {
      button.addEventListener('click', () => {
        const wrap = button.closest('[data-kg-header-wrap]');
        const header = wrap?.querySelector('[data-kg-header]');
        if (!header || !wrap) return;
        header.dataset.menuOpen = 'false';
        wrap.dataset.menuOpen = 'false';
        header.querySelector('[data-kg-menu-toggle]')?.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('kg-menu-lock');
      });
    });

    document.querySelectorAll('.kg-mobile-drawer a').forEach((link) => {
      link.addEventListener('click', () => {
        const wrap = link.closest('[data-kg-header-wrap]');
        const header = wrap?.querySelector('[data-kg-header]');
        if (!header || !wrap) return;
        header.dataset.menuOpen = 'false';
        wrap.dataset.menuOpen = 'false';
        header.querySelector('[data-kg-menu-toggle]')?.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('kg-menu-lock');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      headers.forEach((header) => {
        const wrap = header.closest('[data-kg-header-wrap]');
        header.dataset.menuOpen = 'false';
        if (wrap) wrap.dataset.menuOpen = 'false';
        header.querySelector('[data-kg-menu-toggle]')?.setAttribute('aria-expanded', 'false');
      });
      document.documentElement.classList.remove('kg-menu-lock');
    });

    let knownCartCount = null;
    let optimisticAt = 0;
    const getVisibleCartCount = () => {
      const counter = document.querySelector('.kg-cart-pill__count');
      const match = counter?.textContent?.match(/\d+/);
      return match ? Number(match[0]) : 0;
    };
    const paintCartCount = (count) => {
      if (count == null || Number.isNaN(Number(count))) return;
      knownCartCount = Number(count);
      document.querySelectorAll('.kg-cart-pill__count').forEach((counter) => {
        counter.textContent = `(${knownCartCount})`;
        counter.closest('.kg-cart-pill')?.classList.add('kg-cart-pill--updated');
        window.setTimeout(() => counter.closest('.kg-cart-pill')?.classList.remove('kg-cart-pill--updated'), 420);
      });
    };
    const fetchCartCount = async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        const cart = await response.json();
        return cart.item_count;
      } catch (error) {
        return null;
      }
    };
    const syncCartCount = async (event) => {
      let count = event?.detail?.data?.source === 'product-form-component'
        ? null
        : event?.detail?.data?.itemCount;

      if (count == null) {
        count = await fetchCartCount();
      }

      if (count == null) return;
      paintCartCount(count);
    };

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;

      if (Date.now() - optimisticAt < 800) {
        window.setTimeout(syncCartCount, 350);
        window.setTimeout(syncCartCount, 900);
        return;
      }

      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, Number(quantityInput?.value || 1));
      optimisticAt = Date.now();
      paintCartCount((knownCartCount ?? getVisibleCartCount()) + quantity);
      window.setTimeout(syncCartCount, 350);
      window.setTimeout(syncCartCount, 900);
    }, true);

    document.addEventListener('click', (event) => {
      const addButton = event.target?.closest?.('[data-kg-add], button[name="add"]');
      const form = addButton?.closest?.('form');
      if (!addButton || !(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add') || Date.now() - optimisticAt < 800) return;

      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, Number(quantityInput?.value || 1));
      optimisticAt = Date.now();
      paintCartCount((knownCartCount ?? getVisibleCartCount()) + quantity);
      window.setTimeout(syncCartCount, 350);
      window.setTimeout(syncCartCount, 900);
    }, true);

    document.addEventListener('cart:update', syncCartCount);
    document.addEventListener('cart:change', syncCartCount);
    document.addEventListener('cart:add', syncCartCount);
    window.addEventListener('pageshow', syncCartCount);
  }

  initReveal() {
    const items = document.querySelectorAll('.kg-reveal');
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.14 }
    );

    items.forEach((item) => observer.observe(item));
  }

  initProductForms() {
    document.querySelectorAll('[data-kg-product]').forEach((product) => {
      const select = product.querySelector('[data-kg-variant-select]');
      const price = product.querySelector('[data-kg-price]');
      const inventory = product.querySelector('[data-kg-inventory]');
      const image = product.querySelector('[data-kg-product-image]');
      const addButton = product.querySelector('[data-kg-add]');
      const addButtonPrice = product.querySelector('[data-kg-button-price]');
      const quantityInput = product.querySelector('[data-kg-quantity-input]');
      const quantityValue = product.querySelector('[data-kg-quantity-value]');
      const quantityMinus = product.querySelector('[data-kg-quantity-minus]');
      const quantityPlus = product.querySelector('[data-kg-quantity-plus]');
      const thumbs = product.querySelectorAll('[data-kg-thumb]');
      let addResetTimer = null;

      const setQuantity = (value) => {
        const nextValue = Math.max(1, parseInt(value, 10) || 1);
        if (quantityInput) quantityInput.value = String(nextValue);
        if (quantityValue) quantityValue.textContent = String(nextValue);
      };

      const setAddState = (state) => {
        if (!addButton) return;
        const label = addButton.querySelector('[data-kg-add-label]');
        const priceBadge = addButton.querySelector('[data-kg-button-price]');
        const defaultLabel = addButton.dataset.kgAddDefault || 'Add to Cart';
        const loadingLabel = addButton.dataset.kgAddLoading || 'Adding';
        const successLabel = addButton.dataset.kgAddSuccess || 'Added';

        window.clearTimeout(addResetTimer);
        addButton.classList.toggle('is-loading', state === 'loading');
        addButton.classList.toggle('is-success', state === 'success');
        if (priceBadge) priceBadge.hidden = state !== 'normal';

        if (label) {
          label.textContent = state === 'loading' ? loadingLabel : state === 'success' ? successLabel : defaultLabel;
        }

        if (state === 'success') {
          addResetTimer = window.setTimeout(() => setAddState('normal'), 1600);
        }
      };

      const sync = () => {
        const option = select?.selectedOptions?.[0];
        if (!option) return;
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (addButtonPrice && option.dataset.price) addButtonPrice.textContent = option.dataset.price;
        if (inventory) {
          inventory.textContent =
            option.dataset.available === 'true'
              ? inventory.dataset.inStock || 'In stock'
              : inventory.dataset.soldOut || 'Unavailable';
        }
        if (addButton) addButton.disabled = option.dataset.available !== 'true';
        if (image && option.dataset.image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!image || !thumb.dataset.image) return;
          image.style.opacity = '0';
          window.setTimeout(() => {
            image.src = thumb.dataset.image;
            image.removeAttribute('srcset');
            image.style.opacity = '1';
          }, 120);
          thumbs.forEach((item) => {
            const isActive = item === thumb;
            item.classList.toggle('is-active', isActive);
            item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        });
      });

      select?.addEventListener('change', sync);
      quantityMinus?.addEventListener('click', () => setQuantity((parseInt(quantityInput?.value || '1', 10) || 1) - 1));
      quantityPlus?.addEventListener('click', () => setQuantity((parseInt(quantityInput?.value || '1', 10) || 1) + 1));
      addButton?.closest('form')?.addEventListener('submit', () => {
        if (addButton.disabled) return;
        setAddState('loading');
        window.setTimeout(() => {
          if (addButton.classList.contains('is-loading')) setAddState('normal');
        }, 5000);
      });
      const handleAddSuccess = () => {
        if (!addButton?.classList.contains('is-loading')) return;
        setAddState('success');
      };
      document.addEventListener('cart:update', handleAddSuccess);
      document.addEventListener('cart:change', handleAddSuccess);
      document.addEventListener('cart:add', handleAddSuccess);
      document.addEventListener('cart:error', () => {
        if (addButton?.classList.contains('is-loading')) setAddState('normal');
      });
      setQuantity(quantityInput?.value || 1);
      sync();
    });
  }

  initProductPages() {
    document.querySelectorAll('[data-kg-product-page]').forEach((page) => {
      const form = page.querySelector('.kg-pp-form');
      const variantSelect = page.querySelector('[data-kg-pp-variant-select]');
      const variantInput = page.querySelector('[data-kg-pp-variant-id]');
      const quantityInput = page.querySelector('[data-kg-pp-quantity-input]');
      const quantityValue = page.querySelector('[data-kg-quantity-value]');
      const quantityMinus = page.querySelector('[data-kg-quantity-minus]');
      const quantityPlus = page.querySelector('[data-kg-quantity-plus]');
      const addButton = page.querySelector('.kg-pp-add');
      const mainImage = page.querySelector('[data-kg-pp-main-image]');
      const thumbs = page.querySelectorAll('[data-kg-pp-thumb]');
      const bundleCards = page.querySelectorAll('[data-kg-pp-bundle]');
      const bundleTitle = page.querySelector('[data-kg-pp-bundle-summary-title]');
      const bundleSave = page.querySelector('[data-kg-pp-bundle-summary-save]');
      const bundleAdd = page.querySelector('[data-kg-pp-bundle-add]');
      let addTimer = null;

      const cartRoot = window.Shopify?.routes?.root || '/';

      const setQuantity = (value) => {
        const next = Math.max(1, parseInt(value, 10) || 1);
        if (quantityInput) quantityInput.value = String(next);
        if (quantityValue) quantityValue.textContent = String(next);
      };

      const getVariantId = () => variantSelect?.value || variantInput?.value || bundleAdd?.dataset.variantId;

      const formatCents = (cents) => {
        const value = Math.max(0, Number(cents) || 0) / 100;
        const hasCents = Math.round(value * 100) % 100 !== 0;
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
          minimumFractionDigits: hasCents ? 2 : 0,
          maximumFractionDigits: hasCents ? 2 : 0,
        }).format(value);
      };

      const dispatchCartUpdate = async (fallbackQuantity) => {
        try {
          const response = await fetch(`${cartRoot}cart.js`, { headers: { Accept: 'application/json' } });
          const cart = response.ok ? await response.json() : null;
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { data: { itemCount: cart?.item_count } } }));
        } catch (error) {
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { data: { itemCount: fallbackQuantity } } }));
        }
      };

      const addItems = async ({ variantId, quantity }) => {
        const response = await fetch(`${cartRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data?.description || data?.message || 'Unable to add item');
        document.dispatchEvent(new CustomEvent('cart:add', { detail: { data } }));
        await dispatchCartUpdate(quantity);
        return data;
      };

      const setButtonState = (button, state) => {
        if (!button) return;
        const label = button.querySelector('[data-kg-add-label]') || button.querySelector('span:last-child');
        const normal = button.dataset.kgAddDefault || button.dataset.defaultLabel || 'Add to Cart';
        const loading = button.dataset.kgAddLoading || button.dataset.loadingLabel || 'Adding';
        const success = button.dataset.kgAddSuccess || button.dataset.successLabel || 'Added';

        window.clearTimeout(addTimer);
        button.classList.toggle('is-loading', state === 'loading');
        button.classList.toggle('is-success', state === 'success');
        button.disabled = state === 'loading';
        if (label) label.textContent = state === 'loading' ? loading : state === 'success' ? success : normal;

        if (state === 'success') {
          addTimer = window.setTimeout(() => setButtonState(button, 'normal'), 1800);
        }
      };

      const syncVariant = () => {
        const option = variantSelect?.selectedOptions?.[0];
        if (!option) return;
        const price = page.querySelector('[data-kg-pp-price]');
        const compare = page.querySelector('[data-kg-pp-compare]');
        const addLabel = addButton?.querySelector('[data-kg-add-label]');
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (compare && option.dataset.compare) compare.textContent = Number(option.dataset.compareRaw || 0) > 0 ? option.dataset.compare : '';
        if (addLabel) addLabel.textContent = `${addButton.dataset.kgAddDefault || 'Add to Cart'} — ${option.dataset.price || ''}`.trim();
        if (addButton) addButton.disabled = option.dataset.available !== 'true';
        if (bundleAdd) {
          bundleAdd.dataset.variantId = option.value;
          refreshBundlePricing(Number(option.dataset.priceRaw || 0));
        }
        if (mainImage && option.dataset.image) {
          mainImage.style.opacity = '0';
          window.setTimeout(() => {
            mainImage.src = option.dataset.image;
            mainImage.removeAttribute('srcset');
            mainImage.style.opacity = '1';
          }, 120);
        }
      };

      const updateBundleAddLabel = () => {
        if (!bundleAdd) return;
        const label = bundleAdd.querySelector('span:last-child');
        if (!label) return;
        const baseLabel = bundleAdd.dataset.defaultLabel || 'Add Bundle to Cart';
        const quantity = bundleAdd.dataset.quantity || '1';
        label.textContent = bundleAdd.dataset.showQuantity === 'true' ? `${baseLabel} (${quantity})` : baseLabel;
      };

      const refreshBundlePricing = (basePrice) => {
        if (!bundleAdd || bundleAdd.dataset.dynamicPricing !== 'true' || !basePrice) return;
        bundleCards.forEach((card) => {
          const quantity = Math.max(1, Number(card.dataset.quantity) || 1);
          const discount = Math.max(0, Number(card.dataset.discount) || 0);
          const compareTotal = basePrice * quantity;
          const total = Math.max(0, compareTotal - discount);
          const priceText = formatCents(total);
          const savingsText = discount > 0 ? `Save ${formatCents(discount)}` : card.dataset.savings || '';
          const priceEl = card.querySelector('.kg-pp-bundle-price');
          const compareEl = card.querySelector('s');
          const savingsEl = card.querySelector('.kg-pp-bundle-save');

          card.dataset.price = priceText;
          card.dataset.savings = savingsText;
          if (priceEl) priceEl.textContent = priceText;
          if (compareEl) compareEl.textContent = quantity > 1 && discount > 0 ? formatCents(compareTotal) : '';
          if (savingsEl) savingsEl.textContent = savingsText;
        });

        const selectedBundle = page.querySelector('[data-kg-pp-bundle].is-selected') || bundleCards[0];
        if (selectedBundle) selectBundle(selectedBundle);
      };

      const selectBundle = (card) => {
        if (!card) return;
        bundleCards.forEach((item) => {
          const selected = item === card;
          item.classList.toggle('is-selected', selected);
          const selectLabel = item.querySelector('.kg-pp-bundle-select');
          if (selectLabel) selectLabel.textContent = selected ? 'Selected' : 'Select bundle';
        });
        if (bundleTitle) bundleTitle.textContent = `${card.dataset.title || ''} — ${card.dataset.price || ''}`;
        if (bundleSave) bundleSave.textContent = card.dataset.savings ? `You ${card.dataset.savings.toLowerCase()}` : '';
        if (bundleAdd) {
          bundleAdd.dataset.quantity = card.dataset.quantity || '1';
          updateBundleAddLabel();
        }
        setQuantity(card.dataset.quantity || 1);
      };

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (!mainImage || !thumb.dataset.image) return;
          mainImage.style.opacity = '0';
          window.setTimeout(() => {
            mainImage.src = thumb.dataset.image;
            mainImage.removeAttribute('srcset');
            mainImage.style.opacity = '1';
          }, 120);
          thumbs.forEach((item) => {
            const active = item === thumb;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        });
      });

      quantityMinus?.addEventListener('click', () => setQuantity((parseInt(quantityInput?.value || '1', 10) || 1) - 1));
      quantityPlus?.addEventListener('click', () => setQuantity((parseInt(quantityInput?.value || '1', 10) || 1) + 1));
      variantSelect?.addEventListener('change', syncVariant);
      bundleCards.forEach((card) => card.addEventListener('click', () => selectBundle(card)));

      form?.addEventListener('submit', async (event) => {
        const submitter = event.submitter;
        if (!submitter?.matches?.('[data-kg-add], button[name="add"]')) return;
        event.preventDefault();
        if (!addButton || addButton.disabled) return;
        try {
          setButtonState(addButton, 'loading');
          await addItems({ variantId: getVariantId(), quantity: quantityInput?.value || 1 });
          setButtonState(addButton, 'success');
        } catch (error) {
          setButtonState(addButton, 'normal');
          document.dispatchEvent(new CustomEvent('cart:error', { detail: { error } }));
        }
      });

      bundleAdd?.addEventListener('click', async () => {
        if (bundleAdd.disabled) return;
        const label = bundleAdd.querySelector('span:last-child');
        try {
          bundleAdd.disabled = true;
          bundleAdd.classList.add('is-loading');
          if (label) label.textContent = bundleAdd.dataset.loadingLabel || 'Adding';
          await addItems({ variantId: getVariantId(), quantity: bundleAdd.dataset.quantity || quantityInput?.value || 1 });
          bundleAdd.classList.remove('is-loading');
          bundleAdd.classList.add('is-success');
          if (label) label.textContent = bundleAdd.dataset.successLabel || 'Added';
          window.setTimeout(() => {
            bundleAdd.classList.remove('is-success');
            bundleAdd.disabled = false;
            updateBundleAddLabel();
          }, 1800);
        } catch (error) {
          bundleAdd.classList.remove('is-loading');
          bundleAdd.disabled = false;
          updateBundleAddLabel();
          document.dispatchEvent(new CustomEvent('cart:error', { detail: { error } }));
        }
      });

      const selectedBundle = page.querySelector('[data-kg-pp-bundle].is-selected') || bundleCards[0];
      selectBundle(selectedBundle);
      setQuantity(quantityInput?.value || 1);
      syncVariant();
    });

    document.querySelectorAll('[data-kg-pp-tabs]').forEach((tabs) => {
      const tabButtons = Array.from(tabs.querySelectorAll('[data-kg-pp-tab]'));
      const panels = Array.from(tabs.querySelectorAll('[data-kg-pp-panel]'));

      const activate = (button) => {
        tabButtons.forEach((tab) => tab.setAttribute('aria-selected', tab === button ? 'true' : 'false'));
        panels.forEach((panel) => {
          panel.hidden = panel.id !== button.getAttribute('aria-controls');
        });
      };

      tabButtons.forEach((button) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          const delta = event.key === 'ArrowRight' ? 1 : -1;
          const next = tabButtons[(tabButtons.indexOf(button) + delta + tabButtons.length) % tabButtons.length];
          next.focus();
          activate(next);
        });
      });
    });
  }

  initContactPages() {
    document.querySelectorAll('[data-kg-contact-page]').forEach((page) => {
      const topicInput = page.querySelector('[data-kg-topic-input]');
      const topicPills = Array.from(page.querySelectorAll('[data-kg-topic-pill]'));
      const form = page.querySelector('.kg-contact-form');
      const submitButton = page.querySelector('[data-kg-contact-submit]');
      const submitLabel = page.querySelector('[data-kg-contact-submit-label]');

      topicPills.forEach((pill) => {
        pill.addEventListener('click', () => {
          topicPills.forEach((item) => {
            const active = item === pill;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          if (topicInput) topicInput.value = pill.dataset.topic || pill.textContent.trim();
        });
      });

      form?.addEventListener('submit', () => {
        if (!submitButton) return;
        submitButton.classList.add('is-loading');
        submitButton.disabled = true;
        if (submitLabel) {
          submitLabel.textContent = submitButton.dataset.loadingLabel || 'Sending...';
        }
      });
    });
  }

  initFaqAccordions() {
    document.querySelectorAll('[data-kg-faq]').forEach((accordion) => {
      const items = Array.from(accordion.querySelectorAll('.kg-faq__item'));
      const duration = parseInt(accordion.dataset.duration || '320', 10);
      const singleOpen = accordion.dataset.singleOpen === 'true';
      let firstOpenSeen = false;

      const getContent = (item) => item.querySelector('[data-kg-faq-content]');

      const finishOpen = (content) => {
        content.style.height = 'auto';
        content.style.opacity = '1';
      };

      const openItem = (item) => {
        const content = getContent(item);
        if (!content || item.dataset.animating === 'true') return;

        if (singleOpen) {
          items.forEach((other) => {
            if (other !== item && other.open) closeItem(other);
          });
        }

        item.dataset.animating = 'true';
        item.open = true;
        content.style.height = '0px';
        content.style.opacity = '0';

        requestAnimationFrame(() => {
          content.style.height = `${content.scrollHeight}px`;
          content.style.opacity = '1';
        });

        window.setTimeout(() => {
          finishOpen(content);
          item.dataset.animating = 'false';
        }, duration);
      };

      const closeItem = (item) => {
        const content = getContent(item);
        if (!content || item.dataset.animating === 'true') return;

        item.dataset.animating = 'true';
        content.style.height = `${content.scrollHeight}px`;
        content.style.opacity = '1';

        requestAnimationFrame(() => {
          content.style.height = '0px';
          content.style.opacity = '0';
        });

        window.setTimeout(() => {
          item.open = false;
          item.dataset.animating = 'false';
        }, duration);
      };

      items.forEach((item) => {
        const content = getContent(item);
        const summary = item.querySelector('summary');
        if (!content || !summary) return;

        if (item.open) {
          if (singleOpen && firstOpenSeen) item.open = false;
          firstOpenSeen = true;
        }

        content.style.height = item.open ? 'auto' : '0px';
        content.style.opacity = item.open ? '1' : '0';
        item.dataset.animating = 'false';

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          item.open ? closeItem(item) : openItem(item);
        });
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new KgBwsHomepage());
